import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaPlus, FaSearch, FaSpinner, FaUserFriends, FaTrash } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import debounce from "lodash/debounce";
import toast from 'react-hot-toast';
import ConfirmationModal from "./ConfirmationModal";

function DocumentSidebar({
  activeDocumentId,
  setActiveDocumentId,
  setHasActiveDocument,
}) {
  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const observerTarget = useRef(null);
  const [expandedDocumentId, setExpandedDocumentId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/my-documents?page=${page}&limit=15&search=${searchQuery}`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );
      const { documents: fetchedDocuments, pagination } = response.data;

      setDocuments((prevDocs) =>
        page === 1 ? fetchedDocuments : [...prevDocs, ...fetchedDocuments]
      );

      setHasMore(page < pagination.totalPages);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  }, [page, authState.token, loading, searchQuery]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const currentObserverTarget = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (currentObserverTarget) {
      observer.observe(currentObserverTarget);
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
    };
  }, [hasMore]);

  const debouncedSearch = useRef(
    debounce((term) => {
      setSearchQuery(term);
      setPage(1);
      setDocuments([]);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(
    (e) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch]
  );

  const toggleDocumentDetails = (docId) => {
    setExpandedDocumentId((prevId) => (prevId === docId ? null : docId));
  };

  const createNewDocument = async () => {
    const title = prompt("Enter the title for the new document:");
    if (!title) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/create-doc`,
        { title },
        {
          headers: { "x-auth-token": authState.token },
        }
      );
      const newDocument = response.data;
      setDocuments((prevDocs) => [newDocument, ...prevDocs]);
      navigate(`/documents/${newDocument._id}`);
      setActiveDocumentId(newDocument._id);
      setHasActiveDocument(true);
      toast.success("New document created successfully");
    } catch (error) {
      console.error("Error creating new document:", error);
      toast.error("Failed to create new document");
    }
  };

  const openDeleteModal = (docId) => {
    setDocumentToDelete(docId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/documents/delete/${documentToDelete}`, {
        headers: { "x-auth-token": authState.token },
      });
      
      if (response.data && response.data.msg) {
        toast.success(response.data.msg);
      } else {
        toast.success("Document deleted successfully");
      }
  
      setDocuments((prevDocs) => prevDocs.filter((doc) => doc._id !== documentToDelete));
      if (activeDocumentId === documentToDelete) {
        setActiveDocumentId(null);
        setHasActiveDocument(false);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      if (error.response && error.response.data && error.response.data.msg) {
        toast.error(error.response.data.msg);
      } else {
        toast.error("Failed to delete document. Please try again.");
      }
    } finally {
      closeDeleteModal();
    }
  };

  return (
    <motion.aside
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sidebar w-full px-2 py-4 bg-gray-100 shadow-lg overflow-hidden flex flex-col h-screen"
    >
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={createNewDocument}
        style={{
          boxShadow: "11px 13px 28px 3px rgba(0,0,0,0.35)",
        }}
        className="w-full mb-4 py-3 px-4 bg-gradient-to-r from-slate-800 to-pink-500 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 text-sm font-medium flex items-center justify-center"
      >
        <FaPlus className="mr-2" /> New Document
      </motion.button>

      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Search..."
          onChange={handleSearchChange}
          className="w-full p-3 pl-10 text-sm bg-white text-gray-800 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      </div>

      <div className="flex-grow overflow-y-auto">
        <AnimatePresence>
          {documents.map((doc) => (
            <motion.div
              key={doc._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className={`mb-3 p-3 rounded-lg shadow-md cursor-pointer ${
                doc._id === activeDocumentId
                  ? "bg-purple-100 border-l-4 border-purple-500"
                  : "bg-white hover:bg-gray-50"
              }`}
              onClick={() => {
                navigate(`/documents/${doc._id}`);
                setActiveDocumentId(doc._id);
                setHasActiveDocument(true);
              }}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-800 truncate flex-grow">
                  {doc.title || "Untitled Document"}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteModal(doc._id);
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200 ml-2"
                >
                  <FaTrash size={12} />
                </button>
              </div>
              <div className="flex items-center text-xs text-gray-500 mt-2">
                <span className="truncate flex-grow">
                  {doc.user
                    ? `${doc.user.firstname} ${doc.user.lastname}`
                    : "Unknown"}
                </span>
                <button
                  className="ml-2 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDocumentDetails(doc._id);
                  }}
                >
                  <FaUserFriends className="mr-1" />
                  {doc.collaborators ? doc.collaborators.length : 0}
                </button>
              </div>
              {expandedDocumentId === doc._id && (
                <div className="mt-2 text-xs text-gray-600">
                  <h4 className="font-semibold">Creator:</h4>
                  <p>
                    {doc.user
                      ? `${doc.user.firstname} ${doc.user.lastname}`
                      : "Unknown"}
                  </p>
                  {doc.collaborators && doc.collaborators.length > 0 && (
                    <>
                      <h4 className="font-semibold mt-2">Collaborators:</h4>
                      <ul>
                        {doc.collaborators.map((collab) => (
                          <li key={collab._id}>
                            {collab.firstname} {collab.lastname}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                {new Date(doc.updatedAt).toLocaleString()}
              </p>
            </motion.div>
          ))}
        </AnimatePresence>
        {hasMore && <div ref={observerTarget} className="h-4" />}
      </div>

      {loading && (
        <div className="flex justify-center my-2">
          <FaSpinner className="animate-spin text-indigo-500" />
        </div>
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this document?"
      />
    </motion.aside>
  );
}

export default DocumentSidebar;